import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { ContextMenuProps } from './Types'
import { Menu } from 'components/Menu/Menu'
import { Item, Section } from '@react-stately/collections'
import { useActions } from '../../hooks/useActions'
import { ActionId } from '../../Types'
import { Key, useMemo } from 'react'
import { KeyboardShortcutTag } from 'blaze-ui/KeyboardShortcutTag/KeyboardShortcutTag'
import Styles from './ContextMenu.module.scss'
import { ItemContent } from './ItemContent'
import { track } from '@tldraw/tldraw'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { ItemElement } from '@react-types/shared'

const ContextMenu = track(({ children }: ContextMenuProps) => {
  const actions = useActions()
  const editor = useEditor()

  const selectedShapes = editor.getSelectedShapes()
  const selectedShapesCount = selectedShapes.length
  const hasSelectedShapes = selectedShapesCount > 0
  const hasVideo = selectedShapes.some((shape) => shape.type === 'video')

  const handleAction = (actionId: Key) => {
    actions[actionId as ActionId].onSelect()
  }

  const disabledKeys = useMemo(() => {
    const disabledKeys = new Set<Key>()
    if (selectedShapes.some((shape) => shape.type === 'frame')) {
      disabledKeys.add('send-backward')
      disabledKeys.add('send-to-back')
      disabledKeys.add('bring-forward')
      disabledKeys.add('bring-to-front')
    }
    return disabledKeys
  }, [selectedShapes])

  return (
    <ContextMenuTrigger placement="bottom-start" autoFocus={false}>
      {children}
      {(close) => (
        <Menu
          aria-label="Menu"
          onAction={(key) => {
            handleAction(key)
            close()
          }}
          itemClassName={Styles.menuItem}
          disabledKeys={disabledKeys}
        >
          <Section>
            {
              [
                hasSelectedShapes && !hasVideo && (
                  <Item key="copy" textValue="Copy">
                    <ItemContent>
                      Copy
                      <KeyboardShortcutTag modifiers={['Mod']} keys="c" />
                    </ItemContent>
                  </Item>
                ),
                <Item key="paste" textValue="Paste">
                  <ItemContent>
                    Paste
                    <KeyboardShortcutTag modifiers={['Mod']} keys="v" />
                  </ItemContent>
                </Item>,
                hasSelectedShapes && !hasVideo && (
                  <Item key="duplicate" textValue="Duplicate">
                    <ItemContent>
                      Duplicate
                      <KeyboardShortcutTag modifiers={['Mod']} keys="d" />
                    </ItemContent>
                  </Item>
                ),
                hasSelectedShapes && (
                  <Item key="delete" textValue="Delete">
                    <ItemContent>
                      Delete
                      <KeyboardShortcutTag modifiers={[]} keys="DEL" />
                    </ItemContent>
                  </Item>
                ),
                selectedShapesCount > 1 && (
                  <Item key="group" textValue="Group Selection">
                    <ItemContent>
                      Group Selection
                      <KeyboardShortcutTag modifiers={['Mod']} keys="g" />
                    </ItemContent>
                  </Item>
                ),
                selectedShapes.some(({ type }) => type === 'group') && (
                  <Item key="ungroup" textValue="Ungroup Selection">
                    <ItemContent>
                      Ungroup Selection
                      <KeyboardShortcutTag modifiers={['Mod', 'Shift']} keys="g" />
                    </ItemContent>
                  </Item>
                ),
              ].filter(Boolean) as ItemElement<HTMLElement>[]
            }
          </Section>
          <Section>
            {
              [
                hasSelectedShapes && (
                  <Item textValue="Layer">
                    Layer
                    {
                      // @ts-ignore
                      () => (
                        <Menu
                          onAction={(key) => {
                            close()
                            handleAction(key)
                          }}
                          aria-label="Layer"
                          disabledKeys={disabledKeys}
                        >
                          <Item key="bring-forward" textValue="Bring forward">
                            <ItemContent>
                              Bring forward
                              <KeyboardShortcutTag modifiers={['Mod']} keys="]" />
                            </ItemContent>
                          </Item>
                          <Item key="send-backward" textValue="Send backward">
                            <ItemContent>
                              Send backward
                              <KeyboardShortcutTag modifiers={['Mod']} keys="[" />
                            </ItemContent>
                          </Item>
                          <Item key="bring-to-front" textValue="Bring to front">
                            <ItemContent>
                              Bring to front
                              <KeyboardShortcutTag modifiers={['Mod', 'Opt']} keys="]" />
                            </ItemContent>
                          </Item>
                          <Item key="send-to-back" textValue="Send to back">
                            <ItemContent>
                              Send to back
                              <KeyboardShortcutTag modifiers={['Mod', 'Opt']} keys="[" />
                            </ItemContent>
                          </Item>
                        </Menu>
                      )
                    }
                  </Item>
                ),
                hasSelectedShapes && (
                  <Item textValue="Align">
                    {selectedShapesCount > 1 ? 'Align Elements' : 'Align to Page'}
                    {
                      // @ts-ignore
                      () => (
                        <Menu
                          onAction={(key) => {
                            close()
                            handleAction(key)
                          }}
                          aria-label="Align"
                        >
                          <Item key="align-left" textValue="Align left">
                            Align left
                          </Item>
                          <Item key="align-center-horizontal" textValue="Align center">
                            Align center
                          </Item>
                          <Item key="align-right" textValue="Align right">
                            Align right
                          </Item>
                          <Item key="align-top" textValue="Align top">
                            Align top
                          </Item>
                          <Item key="align-center-vertical" textValue="Align middle">
                            Align middle
                          </Item>
                          <Item key="align-bottom" textValue="Align bottom">
                            Align bottom
                          </Item>
                        </Menu>
                      )
                    }
                  </Item>
                ),
              ].filter(Boolean) as ItemElement<HTMLElement>[]
            }
          </Section>
        </Menu>
      )}
    </ContextMenuTrigger>
  )
})

export { ContextMenu }
